// Archive & clear old bills — the one growth-unbounded collection in the app.
//
// All sales live in a single localStorage array (`sconnect.sales`, see db.ts),
// loaded and rewritten in full on every new bill. As that array grows the app
// slows down and edges toward the ~5 MB localStorage quota, past which a sale
// can silently fail to save. This module lets the operator move bills older
// than a cutoff out of the live array into a downloadable JSON archive file,
// keeping recent bills (and reports) fast while preserving full history off-app.
//
// The archive file is a superset of the plain sales export: it carries the bills
// plus a small `meta` envelope (date range, count, app version marker) so it is
// self-describing and can be merged back later via importArchivedSales().

import { Transaction } from '../types';
import { getSales, saveSales } from './db';

// File format marker, bumped if the envelope shape ever changes.
const ARCHIVE_FORMAT = 'epos-sales-archive/v1';

export interface ArchiveFile {
  format: string;
  meta: {
    createdAt: string;
    cutoff: string; // bills strictly before this ISO instant were archived
    count: number;
    from?: string; // oldest archived bill timestamp
    to?: string; // newest archived bill timestamp
  };
  sales: Transaction[];
}

export interface ArchivePreview {
  // Bills that would be archived (older than the cutoff) vs. kept.
  archiveCount: number;
  keepCount: number;
  totalCount: number;
  // Date range of the bills to be archived (ISO), for display.
  from?: string;
  to?: string;
  // Rough byte size of the bills to be archived, for a "frees ~N KB" hint.
  approxBytes: number;
  // Bills whose timestamp can't be parsed — always kept, never archived.
  undatedCount: number;
}

// A cutoff Date `months` before now (e.g. 6 → bills older than 6 months).
export function cutoffForMonths(months: number): Date {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d;
}

// Parse a bill's timestamp to epoch ms, or null when missing/invalid. Bills that
// fail to parse are treated as un-archivable so a bad date never drops a sale.
function billTime(t: Transaction): number | null {
  const ms = Date.parse(t.timestamp);
  return Number.isNaN(ms) ? null : ms;
}

// Split current sales into [toArchive, toKeep] at the cutoff. A bill is archived
// when it has a valid timestamp strictly older than the cutoff.
function partition(cutoff: Date, sales: Transaction[]): {
  toArchive: Transaction[];
  toKeep: Transaction[];
} {
  const cut = cutoff.getTime();
  const toArchive: Transaction[] = [];
  const toKeep: Transaction[] = [];
  for (const sale of sales) {
    const ms = billTime(sale);
    if (ms !== null && ms < cut) toArchive.push(sale);
    else toKeep.push(sale);
  }
  return { toArchive, toKeep };
}

// Summarise what an archive at this cutoff would move, without changing anything.
export async function previewArchive(cutoff: Date): Promise<ArchivePreview> {
  const sales = await getSales();
  const { toArchive, toKeep } = partition(cutoff, sales);
  const times = toArchive.map(billTime).filter((n): n is number => n !== null);
  const undatedCount = sales.filter((s) => billTime(s) === null).length;
  return {
    archiveCount: toArchive.length,
    keepCount: toKeep.length,
    totalCount: sales.length,
    from: times.length ? new Date(Math.min(...times)).toISOString() : undefined,
    to: times.length ? new Date(Math.max(...times)).toISOString() : undefined,
    approxBytes: toArchive.length ? JSON.stringify(toArchive).length : 0,
    undatedCount,
  };
}

// Build the downloadable archive file for the bills older than `cutoff`. Returns
// null when there is nothing to archive. Does NOT modify local data — the caller
// commits the removal only after the file has been safely saved.
export async function buildArchiveFile(
  cutoff: Date
): Promise<{ filename: string; content: string; count: number } | null> {
  const { toArchive } = partition(cutoff, await getSales());
  if (toArchive.length === 0) return null;
  const times = toArchive.map(billTime).filter((n): n is number => n !== null);
  const from = new Date(Math.min(...times)).toISOString();
  const to = new Date(Math.max(...times)).toISOString();
  const file: ArchiveFile = {
    format: ARCHIVE_FORMAT,
    meta: { createdAt: new Date().toISOString(), cutoff: cutoff.toISOString(), count: toArchive.length, from, to },
    sales: toArchive,
  };
  const filename = `epos-sales-archive_${from.split('T')[0]}_to_${to.split('T')[0]}.json`;
  return { filename, content: JSON.stringify(file, null, 2), count: toArchive.length };
}

// Remove the bills older than `cutoff` from the live sales array. Call this only
// after buildArchiveFile()'s output has been saved. Returns the number removed.
export async function commitArchive(cutoff: Date): Promise<number> {
  const { toArchive, toKeep } = partition(cutoff, await getSales());
  if (toArchive.length === 0) return 0;
  await saveSales(toKeep);
  return toArchive.length;
}

// Merge bills from a previously saved archive file back into the live sales
// array, de-duplicating by id. Accepts both the archive envelope and a plain
// array of transactions. Returns how many were added vs. skipped as duplicates.
export async function importArchivedSales(content: string): Promise<{ added: number; skipped: number }> {
  const parsed = JSON.parse(content) as ArchiveFile | Transaction[];
  const incoming: Transaction[] = Array.isArray(parsed) ? parsed : parsed.sales;
  if (!Array.isArray(incoming)) throw new Error('Not a valid sales archive file.');

  const current = await getSales();
  const seen = new Set(current.map((s) => s.id));
  let added = 0;
  let skipped = 0;
  for (const sale of incoming) {
    if (sale && sale.id && !seen.has(sale.id)) {
      current.push(sale);
      seen.add(sale.id);
      added++;
    } else {
      skipped++;
    }
  }
  if (added > 0) {
    // Keep the merged history in chronological order so reports read naturally.
    current.sort((a, b) => (Date.parse(a.timestamp) || 0) - (Date.parse(b.timestamp) || 0));
    await saveSales(current);
  }
  return { added, skipped };
}
